import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Search, Loader2, MapPin, Star, X, Check, Building } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Off-campus students set a saved "Home" address that becomes their default start point
// (used by Today for class times and by Places for distances).
export default function OffCampusHome({ center, value, complexes, onSaved, onCleared }) {
  const [editing, setEditing] = useState(!value);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);
  const [pending, setPending] = useState(null); // {lat,lng,address}
  const [name, setName] = useState("Home");
  const [savingBusy, setSavingBusy] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { api.get("/me/origins").then((r) => setSaved(r.data)).catch(() => {}); }, []);
  useEffect(() => { setEditing(!value); }, [value]);

  useEffect(() => {
    if (q.trim().length < 3) { setResults([]); return; }
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const near = center ? `&near_lat=${center[0]}&near_lng=${center[1]}` : "";
        const r = await api.get(`/geocode?q=${encodeURIComponent(q.trim())}${near}`);
        setResults(r.data.results || []);
      } catch { setResults([]); } finally { setBusy(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [q, center]);

  const shortLabel = (full) => (full || "").split(",").slice(0, 2).join(",");

  const qq = q.trim().toLowerCase();
  const complexMatches = (complexes || [])
    .filter((c) => c.lat != null && c.lng != null)
    .filter((c) => !qq || (c.name || "").toLowerCase().includes(qq) || (c.address || "").toLowerCase().includes(qq))
    .slice(0, 25);

  const saveHome = async (home) => {
    setSavingBusy(true);
    try {
      const r = await api.put("/me/home-origin", home);
      toast.success(`Home set to "${r.data.label}"`);
      setPending(null); setQ(""); setResults([]); setEditing(false);
      onSaved?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your home address.");
    } finally { setSavingBusy(false); }
  };

  const pickResult = (res) => {
    setPending({ lat: res.lat, lng: res.lng, address: res.label || "" });
    setName("Home");
    setQ(shortLabel(res.label));
    setResults([]);
  };

  const clearHome = async () => {
    try {
      await api.delete("/me/home-origin");
      toast.success("Home address removed");
      onCleared?.();
      setEditing(true);
    } catch { toast.error("Could not remove."); }
  };

  return (
    <div ref={boxRef} data-testid="offcampus-home" className="mb-5 rounded-xl border border-border bg-white overflow-hidden">
      <div className="p-4 sm:p-5">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-md bg-brand/10 text-brand flex items-center justify-center shrink-0">
            <Home size={18} strokeWidth={1.8} />
          </span>
          <div className="min-w-0">
            <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand">Live off campus?</div>
            <h3 className="font-display text-lg font-semibold leading-tight">Set your home address</h3>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          We'll use it as your default start point, so your <b>class times on Today</b> and every distance here start from home.
        </p>

        {/* Current home summary */}
        {value && !editing && (
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-brand/30 bg-brand/5 px-3 py-2.5" data-testid="offcampus-home-current">
            <MapPin size={15} strokeWidth={1.8} className="text-brand shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{value.label}</div>
              {value.address && <div className="text-xs text-muted-foreground truncate">{value.address}</div>}
            </div>
            <button data-testid="offcampus-home-change" onClick={() => { setEditing(true); setQ(""); }}
              className="text-xs font-semibold text-brand hover:underline shrink-0">Change</button>
            <button data-testid="offcampus-home-remove" onClick={clearHome}
              title="Remove home" className="p-1 rounded hover:text-destructive transition-colors shrink-0">
              <X size={15} strokeWidth={1.8} />
            </button>
          </div>
        )}

        {/* Editor */}
        {editing && (
          <div className="mt-4">
            {saved.length > 0 && (
              <div className="mb-3" data-testid="offcampus-home-saved">
                <div className="mono text-[11px] tracking-widest uppercase text-muted-foreground mb-1.5 flex items-center gap-1">
                  <Star size={10} strokeWidth={1.8} /> Use a saved place
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {saved.map((o) => (
                    <button key={o.id} data-testid={`offcampus-home-saved-${o.id}`}
                      onClick={() => saveHome({ label: o.label, lat: o.lat, lng: o.lng, address: o.address || "" })}
                      className="text-xs font-semibold rounded-full border border-border bg-white px-3 py-1.5 hover:border-brand transition-colors">
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 rounded-md border border-border px-2.5 py-2">
              <Search size={15} strokeWidth={1.5} className="text-muted-foreground shrink-0" />
              <input data-testid="offcampus-home-search" value={q}
                onChange={(e) => { setQ(e.target.value); setPending(null); }}
                placeholder="Search your complex or any address…"
                className="w-full text-sm bg-transparent outline-none" />
              {busy && <Loader2 size={14} className="animate-spin text-muted-foreground shrink-0" />}
            </div>

            {/* One-tap: pick your exact apartment complex */}
            {complexMatches.length > 0 && !pending && (
              <div className="mt-2" data-testid="offcampus-home-complexes">
                <div className="mono text-[11px] tracking-widest uppercase text-muted-foreground mb-1.5 flex items-center gap-1">
                  <Building size={10} strokeWidth={1.8} /> Your apartment complex · one tap
                </div>
                <div className="max-h-56 overflow-y-auto rounded-md border border-border divide-y divide-border">
                  {complexMatches.map((c) => (
                    <button key={c.id} data-testid={`offcampus-home-complex-${c.id}`}
                      onClick={() => saveHome({ label: c.name, lat: c.lat, lng: c.lng, address: c.address || "" })}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-secondary transition-colors">
                      <Building size={14} strokeWidth={1.5} className="text-brand shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold truncate">{c.name}</span>
                        {c.address && <span className="block text-xs text-muted-foreground truncate">{c.address}</span>}
                      </span>
                      <span className="shrink-0 text-[11px] mono uppercase tracking-widest text-brand">set</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <AnimatePresence>
              {results.length > 0 && !pending && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="mt-2 max-h-52 overflow-y-auto rounded-md border border-border">
                  <div className="mono text-[11px] tracking-widest uppercase text-muted-foreground px-3 pt-2 pb-1">Other addresses</div>
                  {results.map((res, i) => (
                    <button key={i} data-testid={`offcampus-home-result-${i}`} onClick={() => pickResult(res)}
                      className="w-full text-left flex items-start gap-2 px-3 py-2 hover:bg-secondary transition-colors">
                      <MapPin size={14} strokeWidth={1.5} className="text-brand mt-0.5 shrink-0" />
                      <span className="text-xs leading-snug">{res.label}</span>
                    </button>
                  ))}
                </motion.div>
              )}
              {qq.length >= 3 && results.length === 0 && complexMatches.length === 0 && !busy && !pending && (
                <div className="mt-1.5 px-2 py-2 text-xs text-muted-foreground">No matches — try a fuller address.</div>
              )}
            </AnimatePresence>

            {pending && (
              <div className="mt-3" data-testid="offcampus-home-confirm">
                <div className="mono text-[11px] tracking-widest uppercase text-muted-foreground mb-1.5">Name this place</div>
                <div className="flex items-center gap-1.5">
                  <input data-testid="offcampus-home-name" value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && saveHome({ label: name, ...pending })}
                    className="flex-1 text-sm rounded-md border border-border px-2.5 py-2 outline-none focus:ring-2 focus:ring-brand" />
                  <button data-testid="offcampus-home-save" disabled={savingBusy || !name.trim()}
                    onClick={() => saveHome({ label: name, ...pending })}
                    className="flex items-center gap-1.5 bg-brand text-white rounded-md px-3.5 py-2 text-sm font-semibold hover:opacity-90 transition-colors disabled:opacity-40">
                    {savingBusy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} strokeWidth={2.4} />} Set as home
                  </button>
                </div>
              </div>
            )}

            {value && (
              <button data-testid="offcampus-home-cancel" onClick={() => setEditing(false)}
                className="mt-2 text-xs font-semibold text-muted-foreground hover:text-foreground">Cancel</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
